import { localizedStrings } from '@core/strings'
import { Card, CardProps, Chip } from '@common/material'
import React from 'react'
import { Box, Flex, Text } from '@fundamentals'
import moment from 'moment'
import LocationOnOutlined from '@mui/icons-material/LocationOnOutlined'
import PersonOutlined from '@mui/icons-material/PersonOutlined'
import UpdateOutlined from '@mui/icons-material/UpdateOutlined'
import Flag from '@mui/icons-material/Flag'
import { ObservationBase } from '@core/entities/observation'

type ObservationCardProps = Omit<CardProps, 'id'> & {
  observation: ObservationBase
}

export const ObservationCard: React.FC<ObservationCardProps> = ({
  observation,
  sx = {},
  children,
  ...props
}) => {
  return (
    <Card
      sx={{
        pl: 2,
        pr: 2,
        py: 1.5,
        ...sx,
      }}
      {...props}
    >
      <Flex>
        <Box flex={1}>
          {!!observation.title && (
            <Text fontWeight={500} fontSize='1rem'>
              {observation.title}
            </Text>
          )}
          <Chip
            label={observation.getStatusLabel()}
            color={observation.getStatusChipColor()}
            sx={{ mt: 0.5, mb: 1 }}
            size='small'
          />
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            {!!observation.createdOn && (
              <Flex alignItems='center' sx={{ gap: 3 }}>
                <Flex alignItems='center' sx={{ width: 120 }}>
                  <UpdateOutlined
                    sx={{ fontSize: 16, color: 'action.active' }}
                  />
                  <Text color='text.secondary' sx={{ fontSize: 14, ml: 0.5 }}>
                    {localizedStrings.submitted}
                  </Text>
                </Flex>
                <Text sx={{ fontSize: 14, flex: 1 }}>
                  {moment(observation.createdOn).format(
                    'DD MMM YYYY [at] HH:mm',
                  )}
                </Text>
              </Flex>
            )}
            {!!observation.createdBy && (
              <Flex alignItems='center' sx={{ gap: 3 }}>
                <Flex alignItems='center' sx={{ width: 120 }}>
                  <PersonOutlined
                    sx={{ fontSize: 16, color: 'action.active' }}
                  />
                  <Text color='text.secondary' sx={{ fontSize: 14, ml: 0.5 }}>
                    {localizedStrings.author}
                  </Text>
                </Flex>
                <Text sx={{ fontSize: 14, flex: 1 }}>
                  {observation.createdBy.getFullName()}
                </Text>
              </Flex>
            )}
            {!!observation.location && (
              <Flex alignItems='center' sx={{ gap: 3 }}>
                <Flex alignItems='center' sx={{ width: 120 }}>
                  <LocationOnOutlined
                    sx={{ fontSize: 16, color: 'action.active' }}
                  />
                  <Text color='text.secondary' sx={{ fontSize: 14, ml: 0.5 }}>
                    {localizedStrings.location}
                  </Text>
                </Flex>
                <Text sx={{ fontSize: 14, flex: 1 }}>
                  {observation.location}
                </Text>
              </Flex>
            )}
            {!!observation.priority && (
              <Flex alignItems='center' sx={{ gap: 3 }}>
                <Flex alignItems='center' sx={{ width: 120 }}>
                  <Flag sx={{ fontSize: 16, color: 'action.active' }} />
                  <Text color='text.secondary' sx={{ fontSize: 14, ml: 0.5 }}>
                    {localizedStrings.priority}
                  </Text>
                </Flex>
                <Flex alignItems='center' sx={{ flex: 1 }}>
                  <Box
                    height={12}
                    width={12}
                    borderRadius={50}
                    sx={{
                      backgroundColor: observation.getPriorityColor(),
                      mr: 0.5,
                    }}
                  />
                  <Text sx={{ fontSize: 14 }}>
                    {observation.getPriorityLabel()}
                  </Text>
                </Flex>
              </Flex>
            )}
          </Box>
        </Box>
      </Flex>
      {children}
    </Card>
  )
}
